'use client';
import React, { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import { Landmark, ArrowLeftRight, DollarSign, CheckCircle, AlertCircle, Download, RefreshCw, Eye, XCircle, CreditCard, BookOpen, AlertTriangle, ChevronRight } from 'lucide-react';
import MetricCard from '@/components/ui/MetricCard';
import StatusBadge from '@/components/ui/StatusBadge';
import SectionHeader from '@/components/ui/SectionHeader';
import { useRealtimeAlerts } from '@/hooks/useRealtimeAlerts';
import RealtimeFeedPanel from '@/components/ui/RealtimeFeedPanel';
import { createClient } from '@/lib/supabase/client';

const EscrowPositionChart = dynamic(() => import('./EscrowPositionChart'), { ssr: false });
const SettlementVolumeChart = dynamic(() => import('./SettlementVolumeChart'), { ssr: false });

interface FinanceMetrics {
  totalEscrow: string;
  pendingSettlement: string;
  settledMTD: string;
  distributionsDue: string;
  reconciledLedger: string;
  pendingSettlementCount: number;
  failedTransactions: number;
  feeIncomeMTD: string;
}

interface Campaign {
  id: string;
  name: string;
  campaign_ref: string;
  escrow_balance: number;
  investor_count: number;
  status: string;
  days_remaining: number;
}

interface SettlementBatch {
  id: string;
  batch_ref: string;
  campaign: string;
  issuer: string;
  amount: number;
  investor_count: number;
  status: string;
  priority: string;
  submitted_by: string;
  created_at: string;
}

interface Transaction {
  id: string;
  txn_ref: string;
  txn_type: string;
  amount: number;
  description: string | null;
  status: string;
  created_at: string;
}

const txnTypeColor: Record<string, string> = {
  subscription: 'text-info',
  settlement: 'text-positive',
  distribution: 'text-accent',
  fee: 'text-warning',
  refund: 'text-negative',
};

export default function FinanceDashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'escrow' | 'settlement' | 'ledger' | 'distributions'>('overview');
  const [metrics, setMetrics] = useState<FinanceMetrics>({ totalEscrow: '$0', pendingSettlement: '$0', settledMTD: '$0', distributionsDue: '$0', reconciledLedger: '—', pendingSettlementCount: 0, failedTransactions: 0, feeIncomeMTD: '$0' });
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [settlements, setSettlements] = useState<SettlementBatch[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { alerts, isConnected, unreadCount, markAllRead, markRead, dismissAlert } = useRealtimeAlerts({
    channels: ['transactions', 'settlements', 'realtime_alerts'],
  });

  let supabase = createClient();
  const fmt = (n: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [campaignsRes, settlementsRes, txnRes] = await Promise.all([
        supabase.from('campaigns').select('id, name, campaign_ref, escrow_balance, investor_count, status, days_remaining').eq('status', 'published').order('escrow_balance', { ascending: false }),
        supabase.from('settlement_batches').select('*').order('created_at', { ascending: false }).limit(10),
        supabase.from('transactions').select('*').order('created_at', { ascending: false }).limit(15),
      ]);

      const campData = campaignsRes.data || [];
      const settlData = settlementsRes.data || [];
      const txnData = txnRes.data || [];

      setCampaigns(campData);
      setSettlements(settlData);
      setTransactions(txnData);

      const totalEscrow = campData.reduce((sum, c) => sum + (c.escrow_balance || 0), 0);
      const pendingBatches = settlData.filter(s => s.status === 'pending_approval' || s.status === 'on_hold');
      const pendingAmt = pendingBatches.reduce((sum, s) => sum + (s.amount || 0), 0);
      const completedMTD = settlData.filter(s => s.status === 'completed').reduce((sum, s) => sum + (s.amount || 0), 0);
      const failedTxns = txnData.filter(t => t.status === 'failed').length;
      const feesData = txnData.filter(t => t.txn_type === 'fee' && t.status === 'confirmed').reduce((sum, t) => sum + (t.amount || 0), 0);

      setMetrics({
        totalEscrow: fmt(totalEscrow),
        pendingSettlement: fmt(pendingAmt),
        settledMTD: fmt(completedMTD),
        distributionsDue: fmt(0),
        reconciledLedger: failedTxns === 0 ? '100%' : '99.98%',
        pendingSettlementCount: pendingBatches.length,
        failedTransactions: failedTxns,
        feeIncomeMTD: fmt(feesData),
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load finance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ── Real-time data subscriptions ──────────────────────────────────────────
  useEffect(() => {
    let supabase: any = null;
    const subs: any[] = [];
    try {
      supabase = createClient();
      const handler = () => { fetchData(); };

      subs.push(
        supabase
          .channel('finance-rt-campaigns')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'campaigns' }, handler)
          .subscribe(),
        supabase
          .channel('finance-rt-settlements')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'settlement_batches' }, handler)
          .subscribe(),
        supabase
          .channel('finance-rt-transactions')
          .on('postgres_changes', { event: '*', schema: 'public', table: 'transactions' }, handler)
          .subscribe(),
      );
    } catch {
      // Supabase not configured — subscriptions skipped
    }
    return () => {
      if (supabase) subs.forEach((ch) => supabase.removeChannel(ch));
    };
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight">Finance Portal</h1>
          <p className="text-sm text-muted-foreground mt-1">Escrow · Settlement · Ledger · Distributions · Live data</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="btn-secondary text-xs gap-1.5" disabled={loading}><RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh</button>
          <button className="btn-primary text-xs gap-1.5"><Download size={12} /> Financial Report</button>
        </div>
      </div>

      {error && (
        <div className="alert-banner alert-warning">
          <AlertTriangle size={16} className="flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}

      {metrics.failedTransactions > 0 && (
        <div className="alert-banner alert-warning">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-semibold text-sm">{metrics.failedTransactions} Failed Transaction{metrics.failedTransactions > 1 ? 's' : ''} Require Manual Review</p>
            <p className="text-xs text-muted-foreground mt-0.5">Failed transactions moved to suspense account. Reconciliation action required.</p>
          </div>
          <button className="flex-shrink-0 btn-ghost text-xs gap-1 text-warning" onClick={() => setActiveTab('ledger')}>
            Review <ChevronRight size={11} />
          </button>
        </div>
      )}

      <RealtimeFeedPanel alerts={alerts} isConnected={isConnected} unreadCount={unreadCount} onMarkAllRead={markAllRead} onMarkRead={markRead} onDismiss={dismissAlert} label="Live Settlement & Transaction Feed" maxVisible={5} />

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border overflow-x-auto scrollbar-hide">
        {([
          { key: 'overview', label: 'Overview' },
          { key: 'escrow', label: 'Escrow Positions' },
          { key: 'settlement', label: 'Settlement Queue', badge: metrics.pendingSettlementCount },
          { key: 'ledger', label: 'Transaction Ledger' },
          { key: 'distributions', label: 'Distributions' },
        ] as const).map((tab) => (
          <button key={`fin-tab-${tab.key}`} onClick={() => setActiveTab(tab.key as any)} className={`portal-tab ${activeTab === tab.key ? 'active' : ''}`}>
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className="ml-1.5 text-xs bg-warning/20 text-warning rounded-full px-1.5 py-0.5 font-mono-data">{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <RefreshCw size={24} className="animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground text-sm">Loading finance data...</span>
        </div>
      ) : (
        <>
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
                <div className="glass-card rounded-xl p-4 card-hover lg:col-span-2">
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Total Escrow Held</p>
                    <div className="w-8 h-8 rounded-lg bg-surface-3 flex items-center justify-center text-accent">
                      <Landmark size={16} />
                    </div>
                  </div>
                  <p className="text-4xl font-bold font-mono-data text-foreground mb-1">{metrics.totalEscrow}</p>
                  <p className="text-xs text-muted-foreground mb-3">Across {campaigns.length} active escrow accounts</p>
                  <EscrowPositionChart />
                </div>
                <MetricCard label="Pending Settlement" value={metrics.pendingSettlement} subValue={`${metrics.pendingSettlementCount} batches awaiting approval`} variant="warning" icon={ArrowLeftRight} iconColor="text-warning" mono />
                <MetricCard label="Settled MTD" value={metrics.settledMTD} subValue="Month-to-date processed" trend={18.4} trendLabel="vs last month" variant="positive" icon={CheckCircle} iconColor="text-positive" mono />
                <MetricCard label="Distributions Due" value={metrics.distributionsDue} subValue="Next 30 days" variant="info" icon={DollarSign} iconColor="text-info" mono />
                <MetricCard label="Ledger Reconciled" value={metrics.reconciledLedger} subValue={metrics.failedTransactions > 0 ? `${metrics.failedTransactions} item(s) in suspense` : 'All items reconciled'} variant={metrics.failedTransactions > 0 ? 'warning' : 'positive'} icon={BookOpen} iconColor="text-positive" mono />
                <MetricCard label="Fee Income MTD" value={metrics.feeIncomeMTD} subValue="Platform fees collected" trend={9.2} trendLabel="vs last month" variant="default" icon={CreditCard} iconColor="text-accent" mono />
                <MetricCard label="Failed Transactions" value={String(metrics.failedTransactions)} subValue={metrics.failedTransactions > 0 ? 'Require manual resolution' : 'All clear'} variant={metrics.failedTransactions > 0 ? 'critical' : 'positive'} icon={AlertCircle} iconColor={metrics.failedTransactions > 0 ? 'text-critical' : 'text-positive'} />
              </div>
              <div className="glass-card rounded-xl p-4">
                <SectionHeader title="Settlement Volume — Weekly" subtitle="Total value settled per week" />
                <SettlementVolumeChart />
              </div>
            </div>
          )}

          {activeTab === 'escrow' && (
            <div className="space-y-4">
              <SectionHeader title="Escrow Positions" subtitle={`${campaigns.length} active escrow accounts`} />
              {campaigns.length === 0 ? (
                <div className="glass-card rounded-xl p-8 text-center text-muted-foreground text-sm">No active escrow positions.</div>
              ) : (
                campaigns.map((camp) => (
                  <div key={camp.id} className="glass-card rounded-xl p-4 card-hover">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                          <p className="font-semibold text-foreground">{camp.name}</p>
                          <StatusBadge variant="active" label="Active" dot />
                        </div>
                        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                          <span>Ref: <span className="font-mono-data text-foreground">{camp.campaign_ref}</span></span>
                          <span>Investors: <span className="font-mono-data text-foreground">{camp.investor_count}</span></span>
                          {camp.days_remaining != null && <span>Days remaining: <span className="font-mono-data text-foreground">{camp.days_remaining}</span></span>}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-2xl font-bold font-mono-data text-foreground">{fmt(camp.escrow_balance)}</p>
                        <button className="btn-ghost text-xs gap-1 mt-2"><Eye size={12} /> Details</button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'settlement' && (
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <SectionHeader title="Settlement Queue" subtitle={`${settlements.length} batches · ${metrics.pendingSettlementCount} awaiting approval`} />
                <button className="btn-ghost text-xs gap-1.5"><Download size={13} /> Export</button>
              </div>
              {settlements.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No settlement batches found.</div>
              ) : (
                <div className="divide-y divide-border">
                  {settlements.map((batch) => (
                    <div key={batch.id} className={`px-4 py-4 hover:bg-surface-3/40 transition-colors ${batch.status === 'rejected' ? 'border-l-2 border-l-negative' : batch.priority === 'high' || batch.priority === 'critical' ? 'border-l-2 border-l-warning' : ''}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="font-mono-data text-xs text-accent">{batch.batch_ref}</p>
                            <StatusBadge variant={batch.status === 'completed' ? 'confirmed' : batch.status === 'rejected' ? 'failed' : batch.status === 'executing' ? 'review' : 'pending'} label={batch.status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} dot />
                            {(batch.priority === 'high' || batch.priority === 'critical') && <StatusBadge variant="warning" label="HIGH PRIORITY" />}
                          </div>
                          <p className="text-sm font-semibold text-foreground mb-1">{batch.campaign}</p>
                          <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
                            <span>Amount: <span className="font-mono-data font-bold text-foreground">{fmt(batch.amount)}</span></span>
                            <span>Investors: <span className="font-mono-data">{batch.investor_count}</span></span>
                            <span>Submitted by: {batch.submitted_by}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {batch.status === 'pending_approval' && (
                            <>
                              <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-positive/15 text-positive hover:bg-positive/25 border border-positive/20 transition-colors">
                                <CheckCircle size={12} /> Approve
                              </button>
                              <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-negative/15 text-negative hover:bg-negative/25 border border-negative/20 transition-colors">
                                <XCircle size={12} /> Reject
                              </button>
                            </>
                          )}
                          <button className="btn-ghost p-1.5"><Eye size={13} /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'ledger' && (
            <div className="glass-card rounded-xl overflow-hidden">
              <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                <SectionHeader title="Transaction Ledger" subtitle={`${transactions.length} recent transactions`} />
                <button className="btn-ghost text-xs gap-1.5"><Download size={13} /> Export</button>
              </div>
              {transactions.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">No transactions found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-surface-3">
                      <tr>
                        {['TXN Ref', 'Date', 'Description', 'Type', 'Amount', 'Status'].map((h) => (
                          <th key={`th-led-${h}`} className="table-th">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((txn) => (
                        <tr key={txn.id} className="data-table-row">
                          <td className="table-td font-mono-data text-xs text-accent">{txn.txn_ref}</td>
                          <td className="table-td font-mono-data text-xs text-muted-foreground">{new Date(txn.created_at).toLocaleDateString()}</td>
                          <td className="table-td text-xs text-muted-foreground max-w-xs truncate">{txn.description || '—'}</td>
                          <td className="table-td">
                            <span className={`text-xs font-semibold capitalize ${txnTypeColor[txn.txn_type] || 'text-foreground'}`}>{txn.txn_type}</span>
                          </td>
                          <td className="table-td font-mono-data font-semibold text-foreground">{fmt(txn.amount)}</td>
                          <td className="table-td">
                            <StatusBadge variant={txn.status === 'confirmed' ? 'confirmed' : txn.status === 'failed' ? 'failed' : 'pending'} label={txn.status.charAt(0).toUpperCase() + txn.status.slice(1)} dot />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'distributions' && (
            <div className="glass-card rounded-xl p-8 text-center">
              <DollarSign size={32} className="mx-auto text-muted-foreground mb-3" />
              <p className="text-sm font-medium text-foreground">Distribution Schedule</p>
              <p className="text-xs text-muted-foreground mt-1">Distribution records will appear here once campaigns close and distributions are scheduled.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}